import { AxiosResponse } from 'axios';
import { FacebookAccessToken } from './../../domain/facebook';
import {
  FacebookRegisterApiSuccess,
  FacebookRegisterErrors,
  FacebookRegisterError,
  FacebookConfirmResult,
  FacebookConfirmErrorTypes,
  FacebookConfirmEmailAlreadyRegisteredError,
  FacebookConfirmGeneralError
} from './../../application/usecases/facebookInteractor';
import ApiClient from './client';
import { PromiseEither, Either, Left, Right } from '../../utils/either';
import {
  EmailNullableSecureUserData,
  SecureUserErrorData,
  getSecureUserErrorContent
} from './models';
import { Option } from '../../utils/option';
import { UnknownNetworkError } from '../../application/commonModels/apiResponse';
import { Email } from '../../domain/user';
import { convertWaServiceToTrackerRegisteredVia } from '../../utils/registeredVia';
import { WA_SERVICE } from '../../domain/waService';

const register = async (
  facebookAccessToken: FacebookAccessToken,
  waService: WA_SERVICE
): PromiseEither<FacebookRegisterErrors, FacebookRegisterApiSuccess> => {
  const res = await ApiClient.instance.post<EmailNullableSecureUserData>(
    '/auth/facebook/registrations',
    {
      secure_user: {
        secure_facebook_account: {
          token: facebookAccessToken
        }
      },
      registered_via: convertWaServiceToTrackerRegisteredVia(waService)
    },
    'wapi'
  );
  return res
    .leftMap<FacebookRegisterErrors>((e) =>
      Option<AxiosResponse<SecureUserErrorData>>(e.response)
        .flatMap(({ data }) =>
          getSecureUserErrorContent(Option(data)).map(
            (error) => new FacebookRegisterError(error)
          )
        )
        .getOrElse<FacebookRegisterErrors>(() => new UnknownNetworkError())
    )
    .map<FacebookRegisterApiSuccess>(({ status, data }) => {
      const { confirmed_at, email } = data.secure_user;
      return {
        sessionType:
          status === 201 || confirmed_at == null ? 'signup' : 'signin',
        accessToken: data.secure_user.access_token,
        refreshToken: data.secure_user.refresh_token,
        userId: data.user.id,
        facebookAccessToken,
        email: Option(email)
      };
    });
};

const confirm = async (email: string, facebookToken: string) => {
  const res = await ApiClient.instance.patch<EmailNullableSecureUserData>(
    '/auth/facebook/confirmations',
    {
      secure_user: {
        email,
        secure_facebook_account: {
          token: facebookToken
        }
      }
    },
    'wapi'
  );

  return res
    .map<FacebookConfirmResult>(() => ({ type: 'emailSameAsFacebookMail' }))
    .leftFlatMap(
      (e): Either<FacebookConfirmErrorTypes, FacebookConfirmResult> =>
        Option<AxiosResponse<SecureUserErrorData>>(e.response)
          .map(
            ({
              status,
              data
            }): Either<FacebookConfirmErrorTypes, FacebookConfirmResult> => {
              switch (status) {
                case 403: // facebookから取得したメアドをそのまま入力したがすでに他のユーザがそのメアドを登録されている場合
                  return new Left(
                    new FacebookConfirmEmailAlreadyRegisteredError()
                  );
                case 409: // facebookから取得したメアドでないものを入力した場合
                  return new Right<
                    FacebookConfirmErrorTypes,
                    FacebookConfirmResult
                  >({
                    type: 'emailDifferentFromFacebookMail'
                  });
                case 401: // accessTokenがない場合(基本的には起きない)
                  return new Left(new UnknownNetworkError());
                default:
                  return new Left(
                    getSecureUserErrorContent(Option(data))
                      .map((error) => new FacebookConfirmGeneralError(error))
                      .getOrElse<FacebookConfirmErrorTypes>(
                        () => new UnknownNetworkError()
                      )
                  );
              }
            }
          )
          .getOrElse(() => new Left(new UnknownNetworkError()))
    );
};

const sendConfirmationMail = async (email: Email, redirectUrl: string) => {
  const res = await ApiClient.instance.patch(
    '/auth/confirmations',
    {
      email,
      redirect_url: redirectUrl
    },
    'wapi'
  );

  return res
    .leftMap<FacebookConfirmErrorTypes>((e) =>
      Option<AxiosResponse<SecureUserErrorData>>(e.response)
        .map(({ data, status }) => {
          if (status === 403) {
            return new FacebookConfirmEmailAlreadyRegisteredError();
          }
          return getSecureUserErrorContent(Option(data))
            .map((error) => new FacebookConfirmGeneralError(error))
            .getOrElse<FacebookConfirmErrorTypes>(
              () => new UnknownNetworkError()
            );
        })
        .getOrElse<UnknownNetworkError>(() => new UnknownNetworkError())
    )
    .map<FacebookConfirmResult>(() => ({
      type: 'checkEmailBox'
    }));
};

export default {
  register,
  confirm,
  sendConfirmationMail
};
